<template>
  <div>
    <RawRecords v-if="showRawOutput" :records="records" />
    <table v-else-if="processedRecords.length !== 0">
      <colgroup>
        <col style="min-width: 35px;">
        <col style="width: 70%;">
        <col style="width: 30%;">
      </colgroup>
      <thead>
        <tr class="border-b-2 border-gray-200 text-left">
          <th scope="col" />
          <th scope="col" class="py-1.5 font-medium">{{ columnTitle }}</th>
          <th scope="col" class="py-1.5 font-medium">Revalidate in</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(record, index) in processedRecords" :key="index">
          <tr class="group">
            <td v-if="record.ipInfo" class="text-center" @click.stop="e => { e.target.closest('tr').nextSibling.classList.toggle('hidden'); e.target.closest('tr').querySelector('svg').classList.toggle('rotate-90') }">
              <svg width="1em" height="1em" viewBox="0 0 16 16" class="inline" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </td>
            <td v-else />
            <td class="py-1">
              <img v-if="record.ipInfo" class="inline w-4 h-4 mb-0.5 mr-1.5" :src="`https://icons.duckduckgo.com/ip3/${record.ipInfo.asdomain}.ico`"
                   :alt="`Hosted by ${record.ipInfo.asname}`" :title="`Hosted by ${record.ipInfo.asname}`">
              <span>{{ getIp(record.record) }}</span>
              <CnameChainBadge :chain="record.cnameChain" />
              <copy :content="getIp(record.record)" class="inline-block -mt-2 invisible group-hover:visible" />
            </td>
            <td>
              <duration :seconds="record.record.ttl" />
            </td>
          </tr>
          <tr class="hidden" v-if="record.ipInfo">
            <td colspan="3">
              <div class="bg-neutral-50 pb-4 rounded-sm overflow-hidden">
                <div class="w-full h-32 bg-center" :style="{ backgroundImage: `url(https://stadiamaps.com/static/outdoors?api_key=23fe682b-ac7c-4f2b-83db-39dcf2a8b9ac&amp;center=${record.ipInfo.lat + 2},${record.ipInfo.lon}&amp;zoom=2&amp;markers=${record.ipInfo.lat},${record.ipInfo.lon}&amp;size=1038x128)` }" />
                <span class="block m-6 ml-8 mb-4 text-lg font-medium">{{ record.ipInfo.org }}</span>
                <table class="w-full mx-6 border-separate border-spacing-2">
                  <tbody>
                    <tr>
                      <th scope="row" class="text-left font-medium">Location</th>
                      <td><a :href="`https://www.google.com/maps/search/${record.ipInfo.city}, ${record.ipInfo.regionName}, ${record.ipInfo.country}`" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 hover:underline">{{ record.ipInfo.city }}, {{ record.ipInfo.regionName }}, {{ record.ipInfo.country }}</a></td>
                    </tr>
                    <tr>
                      <th scope="row" class="text-left font-medium">AS</th>
                      <td>{{ record.ipInfo.as }}</td>
                    </tr>
                    <tr>
                      <th scope="row" class="text-left font-medium whitespace-nowrap">AS name</th>
                      <td>{{ record.ipInfo.asname }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <div v-else>No {{ recordType }} records found.</div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import { useStore } from 'vuex';
import Copy from '@/components/micro/CopyComponent.vue';
import Duration from '@/components/micro/DurationComponent.vue';
import RawRecords from '@/components/micro/RawRecords.vue';
import CnameChainBadge from '@/components/micro/CnameChainBadge.vue';
import { A, AAAA, QueryResult } from '@/model/Api';

const props = defineProps({
  records: {
    type: Object as PropType<QueryResult>,
    required: true,
  },
  columnTitle: {
    type: String,
    required: true,
  },
  recordType: {
    type: String,
    required: true,
  },
});

const store = useStore();
const showRawOutput = computed(() => store.state.showRawOutput);

const processedRecords = computed(() => props.records.response.answer.filter((item) => ['A', 'AAAA'].includes(item.record.recordType)));

function getIp(record: A | AAAA): string {
  if ('ipv4' in record) return record.ipv4;
  return record.ipv6.slice(1, -1);
}
</script>
